export interface DataTeamService {
  id: string;
  title: string; // Plain English, no jargon
  tagline: string; // One-sentence problem statement
  description: string; // 2-3 sentences on what it shows and when to use it
  previewImage: string;
  timeEstimate: string; // "Half day", "1 day", "2-3 days"
  whatYouProvide: string[]; // Consultant inputs in plain language
  contactPerson: 'Susan' | 'Roland' | 'Giovanni' | 'Younes';
  tags: string[]; // Simple keywords for filtering
  status: 'available' | 'coming-soon';
  relatedInnoFlashes?: string[]; // IDs of related InnoFlashes
}

export const dataTeamServices: DataTeamService[] = [
  {
    id: 'passenger-flows',
    title: 'OV Passenger Flow Visualization',
    tagline: 'See where people get on and off transit lines',
    description:
      'Creates arc diagrams showing boarding and alighting patterns at each stop along bus, tram, or metro lines. Outputs include PDF visualizations and geographic data (GeoPackage) for QGIS or MapHub integration. Processing takes ~5-10 minutes.',
    previewImage: '/assets/services/ov-instappers-uitstappers.png',
    timeEstimate: 'Half day',
    whatYouProvide: [
      'OV chipcard transaction data (Excel file from client)',
      'GTFS transit schedule (we can help locate)',
      'Line numbers to analyze'
    ],
    contactPerson: 'Susan',
    tags: ['Transit', 'Ridership', 'Visualization', 'Arc Diagram'],
    status: 'available',
  },
  {
    id: 'travel-relations',
    title: 'Transit Travel Relations Diagram ("Regenboog")',
    tagline: 'Visualize origin-destination flows on transit lines',
    description:
      'Creates colorful "rainbow" arc diagrams showing where passengers travel from and to along transit routes. Outputs include PDF visualizations and geographic data (GeoPackage) for QGIS or MapHub integration.',
    previewImage: '/assets/services/ov-hb-regenboog.png',
    timeEstimate: 'Half day',
    whatYouProvide: [
      'OV chipcard transaction data (Excel file from client)',
      'GTFS transit schedule',
      'Zone definitions (optional - we can provide defaults)'
    ],
    contactPerson: 'Susan',
    tags: ['Transit', 'Origin-Destination', 'Zones', 'Arc Diagram'],
    status: 'available',
  },
  {
    id: 'ov-passage-frequency',
    title: 'OV Vehicle Passage Frequency Maps',
    tagline: 'Visualize how many buses and trams pass through each street',
    description:
      'Creates heat maps showing the number of bus and tram passages per hour on each street segment. Useful for understanding transit density, planning bus lanes, and assessing neighborhood impacts. Can compare GTFS schedule data with traffic model predictions.',
    previewImage: '/assets/services/ov-passage-frequency.png',
    timeEstimate: 'Half day',
    whatYouProvide: [
      'Study area boundaries',
      'GTFS transit schedule data',
      'Time periods to analyze (e.g., peak hours, full day)',
      'Traffic model data (optional, for comparison)'
    ],
    contactPerson: 'Younes',
    tags: ['Transit', 'GTFS', 'Frequency', 'Heat Map', 'Bus Lanes'],
    status: 'available',
    relatedInnoFlashes: ['07'],
  },
  {
    id: 'ov-speed-frequency',
    title: 'Transit Schedule Speed & Frequency Analysis',
    tagline: 'Identify slow segments and major transit corridors',
    description:
      'Visualizes transit network with focus on: a) line speeds, b) dwell times at stops, and c) service frequency. Creates line maps showing where routes bundle together (high-frequency corridors) and where speeds are low. Works with GTFS schedule data for any time period. Based on InnoFlash #18 methodology.',
    previewImage: '/assets/services/ov-speed-frequency.png',
    timeEstimate: 'Half day',
    whatYouProvide: [
      'GTFS transit schedule data',
      'Study area or specific lines to analyze',
      'Time periods of interest (weekday, weekend, peak hours)',
      'Preferred zoom level (detail or overview)'
    ],
    contactPerson: 'Roland',
    tags: ['Transit', 'GTFS', 'Speed', 'Frequency', 'Network Analysis'],
    status: 'available',
    relatedInnoFlashes: ['18', '07'],
  },
  {
    id: 'multimodal-catchment',
    title: 'Multimodal Catchment Areas (Isochrones)',
    tagline: 'Map how far people can travel in X minutes',
    description:
      'Creates travel time isochrone maps showing areas reachable from a starting point by transit, bike, walking, or combinations. Visualizes fastest travel times using GTFS schedule data. Perfect for station area planning and accessibility analysis. Based on InnoFlash #02 methodology. Currently being refined - contact Susan for availability.',
    previewImage: '/assets/services/multimodal-isochrones.png',
    timeEstimate: '1 day',
    whatYouProvide: [
      'Starting locations (addresses or coordinates)',
      'Travel time limits (e.g., 15, 30, 45 minutes)',
      'Modes to include (bike, walk, transit)',
      'GTFS transit schedule',
      'Date and time period for analysis'
    ],
    contactPerson: 'Susan',
    tags: ['Accessibility', 'Catchment', 'Multimodal', 'Isochrones'],
    status: 'coming-soon',
    relatedInnoFlashes: ['02'],
  },
];
